import { useEffect, useState } from 'react'
import { Button, Steps } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getManagers } from '@/api'
import { createPortfolio } from '@/api/portfolio'
import { useFeedback } from '@/utils/antdApp'
import ChatLayout from '@/Layout/ChatLayout'
import type { ManagerDetail } from '@/types'
import ManagerStep, { type ManagerStepValue } from './components/ManagerStep'
import ConfigStep, { type ConfigValue, createDefaultConfig } from './components/ConfigStep'
import ReviewStep from './components/ReviewStep'
import './components/ManagerStep/index.scss'
import './components/ConfigStep/index.scss'
import './components/ReviewStep/index.scss'
import './index.scss'

const steps = [
    { title: '选择策略' },
    { title: '配置参数' },
    { title: '确认创建' },
]

export default function CreateAssetPortfolioPage() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const presetManagerId = Number(searchParams.get('manager_id')) || null
    const { message } = useFeedback()
    const [step, setStep] = useState(0)
    const [managers, setManagers] = useState<ManagerDetail[]>([])
    const [managersLoading, setManagersLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    const [managerStep, setManagerStep] = useState<ManagerStepValue>({
        manager: null,
        strategyVersion: null,
        stockPoolId: null,
        stockPoolName: '',
        tradeType: 'paper',
    })
    const [configStep, setConfigStep] = useState<ConfigValue>(createDefaultConfig())

    useEffect(() => {
        setManagersLoading(true)
        getManagers()
            .then(list => setManagers(list ?? []))
            .catch(err => message.error(err instanceof Error ? err.message : '加载基金经理失败'))
            .finally(() => setManagersLoading(false))
    }, [])

    const handleNext = async () => {
        if (step === 0) {
            if (!managerStep.manager) { message.error('请选择组合经理'); return }
            if (!managerStep.strategyVersion?.id) { message.error('请选择策略版本（已激活或已归档）'); return }
            const suggestedMarket = (['CN', 'HK', 'US'] as const).includes(
                managerStep.strategyVersion!.market as 'CN' | 'HK' | 'US',
            )
                ? (managerStep.strategyVersion!.market as 'CN' | 'HK' | 'US')
                : 'HK'
            setConfigStep(prev => ({
                ...prev,
                name: prev.name || `${managerStep.manager!.nickname}的组合`,
                // 从策略步进入时用策略版本市场作初始建议，用户可在本步改
                market: suggestedMarket,
                stockPoolId: managerStep.stockPoolId,
                stockPoolName: managerStep.stockPoolName,
                strategyParams: (managerStep.strategyVersion!.strategy_params?.params ?? []).map(p => ({ ...p })),
            }))
            setStep(1)
        } else if (step === 1) {
            if (!configStep.name.trim()) { message.error('请输入组合名称'); return }
            if (!configStep.market) { message.error('请选择交易市场'); return }
            if (!configStep.startDate) { message.error('请选择建仓开始时间'); return }
            if (!configStep.stockPoolId) { message.error('请选择股票池'); return }
            setStep(2)
        } else {
            if (!configStep.stockPoolId) { message.error('请选择股票池'); return }
            await handleSubmit()
        }
    }

    const handleSubmit = async () => {
        setSubmitting(true)
        try {
            await createPortfolio({
                name: configStep.name,
                description: configStep.description,
                manager_id: managerStep.manager!.id,
                stock_pool_id: configStep.stockPoolId!,
                strategy_version_id: managerStep.strategyVersion!.id,
                initial_cash: configStep.cash * 10000,
                market: configStep.market,
                risk_params: {
                    trade_type: managerStep.tradeType,
                    start_date: configStep.startDate,
                    benchmark: configStep.benchmark || undefined,
                    max_positions: configStep.maxPositions,
                    rebalance_frequency: configStep.rebalanceFrequency,
                    max_single_weight: configStep.maxSingleWeight,
                    cash_reserve: configStep.cashReserve,
                    position_sizing: configStep.positionSizing,
                    max_drawdown: configStep.maxDrawdown,
                    sector_limit: configStep.sectorLimit,
                    use_monitor: configStep.useMonitor,
                    use_research: configStep.useResearch,
                    reinvest_dividend: configStep.reinvestDividend,
                    review_pending: managerStep.tradeType === 'live',
                    strategy_params: configStep.strategyParams,
                },
            })
            message.success(managerStep.tradeType === 'live' ? '已提交审核，请等待审批通过' : '组合创建成功')
            navigate('/asset-mgmt')
        } catch (err) {
            message.error(err instanceof Error ? err.message : '创建失败')
        } finally {
            setSubmitting(false)
        }
    }

    const step0Ready = !!(managerStep.manager && managerStep.strategyVersion?.id)
    const submitLabel = step < 2
        ? `下一步：${steps[step + 1].title}`
        : managerStep.tradeType === 'live' ? '提交审核' : '✓ 确认创建'

    return (
        <ChatLayout
            contentWrapped={false}
            className="asset-create"
            content={
                <div className="asset-create__container">
                    <div className="page-header asset-create__header">
                        <div className="asset-create__header-left">
                            <Button size="small" type="text" icon={<ArrowLeftOutlined />} className="text-12"
                                onClick={() => navigate('/asset-mgmt')}>
                                全部组合
                            </Button>
                            /<span className="asset-create__title font-weight-700">新建资产组合</span>
                        </div>
                        <div className="asset-create__header-actions">
                            {step > 0 && (
                                <Button type="text" className="text-12" onClick={() => setStep(s => s - 1)}>
                                    <ArrowLeftOutlined /> {steps[step - 1].title}
                                </Button>
                            )}
                            <Button
                                type="primary"
                                className="text-12"
                                loading={submitting}
                                disabled={step === 0 && !step0Ready}
                                onClick={handleNext}
                            >
                                {submitLabel}
                            </Button>
                        </div>
                    </div>

                    <div className="page-content asset-create__content">
                        <div className="asset-create__steps">
                            <Steps
                                size="small"
                                current={step}
                                items={steps.map((s, i) => ({
                                    title: <span className={i === step ? 'font-weight-700' : undefined}>{s.title}</span>,
                                    icon: i < step ? <span className="font-weight-700">✓</span> : '',
                                }))}
                            />
                        </div>
                        {step === 0 && (
                            <ManagerStep
                                value={managerStep}
                                onChange={setManagerStep}
                                managers={managers}
                                loading={managersLoading}
                                presetManagerId={presetManagerId}
                            />
                        )}
                        {step === 1 && (
                            <ConfigStep
                                value={configStep}
                                onChange={setConfigStep}
                                strategyVersion={managerStep.strategyVersion}
                            />
                        )}
                        {step === 2 && (
                            <ReviewStep managerStep={managerStep} configStep={configStep} />
                        )}
                    </div>
                </div>
            }
            sider={
                <div className="asset-create__tips">
                    <div className="asset-create__tips-title">创建提示</div>
                    {step === 0 && (
                        <>
                            <p>选择一位组合经理后，可从其<strong>已激活或已归档</strong>的策略版本中选择一个用于创建组合。</p>
                            <p>默认优先选中当前激活版本；策略内容只读，如需调整请前往「投资策略回测」模块。</p>
                        </>
                    )}
                    {step === 1 && (
                        <>
                            <p>配置组合的基本参数与风控规则。</p>
                            <p><strong>交易市场</strong>决定模拟盘币种、交易时段与可交易标的范围，与所选策略版本保持一致，不可单独修改。</p>
                            <p><strong>初始资金</strong>为组合启动时的投入金额（单位：万元）。</p>
                            <p><strong>交易设定</strong>中的参数将直接影响 AI 的仓位管理与调仓行为。</p>
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <p>请仔细核对以下信息，确认无误后提交。</p>
                            {managerStep.tradeType === 'live' && (
                                <p className="asset-create__tips-warn">
                                    实盘组合提交后将进入<strong>审核流程</strong>，审核通过后方可开始运营。
                                </p>
                            )}
                        </>
                    )}
                </div>
            }
        />
    )
}
